import React, { FC, useContext } from 'react';
import { LazyQueryResult } from '@apollo/client';
import { aboveBreakpoint, Box, Theme, useMediaQuery } from '@bechtel/gds';
import {
  CrewMember,
  WorkPackageStats,
  WorkPackageStatsInput,
  WorkPackageFilterByTerm,
  MilestoneFilterStatusFlag,
} from '@bechtel/types';
import { get } from 'lodash';
import { ThemeContext } from 'styled-components';
import { CacheKeys, useAppUser, useSessionStorage, useConfig, useModulePermission } from '@bechtel/client-common';
import { Carousel, StatBlock, ShiftLog, useDashboardPermissions } from '@bechtel/client-dashboard-common';
import { dashBoardStatsMock } from '~/form-data';
import { StatBlockStyled } from '~/styled';
import { isWorkPackagePersona } from '~/utilities';
import { WeeklyWorklist } from '~/worklist';
import { WorkpackageList } from '~/workpackage-list';
import { drawerValues as defaultValues, DrawerValuesProps } from '@bechtel/client-work-packages';
import { BlockDrawerValuesProps, defaultValues as blockDefaultValues } from '@bechtel/client-blocks';
import {
  DashBoardLeftSectionStyled,
  ForemanDashboardContentStyled,
  ForemanDashboardStatStyled,
  SectionListStyled,
  WeeklyWorkListContainerStyled,
  WorkPackageContainerStyled,
} from './styled';
import { useNavigate } from 'react-router-dom';
import { t } from '@lingui/macro';

interface PipingForemanDashboardContentProps {
  crewMember: CrewMember | null | undefined;
  result: LazyQueryResult<{ workPackageStats: WorkPackageStats }, { input: WorkPackageStatsInput }>;
}

export const ForemanDashboardContent: FC<PipingForemanDashboardContentProps> = ({ crewMember, result }) => {
  const { canViewBlockModule, canViewWorkPackageModule } = useModulePermission();
  const { canViewDownloadWeeklyWorkList } = useDashboardPermissions();
  const navigate = useNavigate();
  const { user } = useAppUser();
  const { breakpoints } = useContext<Theme>(ThemeContext);
  const isWideScreen = useMediaQuery(aboveBreakpoint(breakpoints.widescreen));
  const { features } = useConfig();
  const setDrawerValues = useSessionStorage<DrawerValuesProps>(CacheKeys.WorkPackageDrawerValues)[1];
  const setCursor = useSessionStorage<number>(CacheKeys.WorkPackageListCursor)[1];
  const setBlockDrawerValues = useSessionStorage<BlockDrawerValuesProps>(CacheKeys.BlockDrawerValues)[1];
  const { called, loading, data } = result;
  const foremanDashboardStats =
    crewMember && crewMember.persona && isWorkPackagePersona(crewMember?.persona)
      ? get(data, 'workPackageStats', dashBoardStatsMock)
      : dashBoardStatsMock;
  const { crew, numWorkPackages, numBlocks, numRejects, plannedToGoMilestones, earnedMilestones, approvedMilestones } =
    foremanDashboardStats;

  const navigateToWorkPackage = async (values: DrawerValuesProps) => {
    if (user && user.crewMember && !isWorkPackagePersona(user.crewMember.persona)) {
      //TODO: Enable filter by owners in drawer
      // const filterByOwners = {
      //   persona: crewMember.persona,
      //   crewMemberId: crewMember.id,
      // };
      values = { ...values, searchScope: false };
    }
    setCursor(1);
    await setDrawerValues(values);
    navigate('work-packages');
  };
  const statsLoading =
    (!called && crewMember && crewMember.persona && isWorkPackagePersona(crewMember?.persona)) || loading;
  return (
    <ForemanDashboardContentStyled>
      <ForemanDashboardStatStyled>
        <StatBlock
          value={crew && crew.numCrewMembers ? crew.numCrewMembers.toString() : '0'}
          label={t`Head Count`}
          icon="user-hard-hat"
          loading={statsLoading}
        />
        <StatBlockStyled
          value={String(numWorkPackages)}
          label={t`Work Packages`}
          icon="briefcase"
          style={{ cursor: canViewWorkPackageModule && numWorkPackages > 0 ? `pointer` : `default` }}
          onClick={async () => {
            if (canViewWorkPackageModule && numWorkPackages > 0) {
              navigateToWorkPackage({ ...defaultValues, filterBy: WorkPackageFilterByTerm.ALL });
            }
          }}
          loading={statsLoading}
        />
        <StatBlockStyled
          value={String(numBlocks)}
          label={t`Blocks`}
          icon="exclamation-triangle"
          style={{ cursor: canViewBlockModule && numBlocks > 0 ? `pointer` : `default` }}
          onClick={async () => {
            if (canViewBlockModule && numBlocks > 0) {
              await setBlockDrawerValues(blockDefaultValues);
              navigate('blocks');
            }
          }}
          loading={statsLoading}
        />

        <StatBlockStyled
          value={String(numRejects)}
          label={t`Rejections`}
          icon="comment-alt-times"
          onClick={() => {
            navigateToWorkPackage({ ...defaultValues, filterBy: MilestoneFilterStatusFlag.REJECTED });
          }}
          loading={statsLoading}
        />
        <StatBlockStyled
          value={
            plannedToGoMilestones && plannedToGoMilestones.numMilestones
              ? plannedToGoMilestones.numMilestones.toString()
              : '0'
          }
          label={t`Planned`}
          subLabel={t`Milestones`}
          valueText="to go"
          onClick={() => {
            navigateToWorkPackage({ ...defaultValues, filterBy: WorkPackageFilterByTerm.IN_3WLA });
          }}
          loading={statsLoading}
        />
        <StatBlock
          value={earnedMilestones && earnedMilestones.numMilestones ? earnedMilestones.numMilestones.toString() : '0'}
          label={t`Claimed`}
          subLabel={t`Milestones`}
          loading={statsLoading}
        />
        <StatBlock
          value={
            approvedMilestones && approvedMilestones.numMilestones ? approvedMilestones.numMilestones.toString() : '0'
          }
          label={t`Approved`}
          subLabel={t`Milestones`}
          loading={statsLoading}
        />
      </ForemanDashboardStatStyled>
      {isWideScreen ? (
        <SectionListStyled>
          <WorkPackageContainerStyled>
            <DashBoardLeftSectionStyled>
              <WorkpackageList crewMember={crewMember}></WorkpackageList>
              {features?.showShiftLog && (
                <Box mt={{ sm: '500' }}>
                  <ShiftLog></ShiftLog>
                </Box>
              )}
            </DashBoardLeftSectionStyled>
          </WorkPackageContainerStyled>
          <WeeklyWorkListContainerStyled>
            <WeeklyWorklist showDownload={canViewDownloadWeeklyWorkList} showOwners={false} crewMember={crewMember} />
          </WeeklyWorkListContainerStyled>
        </SectionListStyled>
      ) : (
        <Carousel>
          <WorkpackageList crewMember={crewMember} />
          <WeeklyWorklist showDownload={canViewDownloadWeeklyWorkList} showOwners={false} crewMember={crewMember} />
          {features?.showShiftLog && <ShiftLog></ShiftLog>}
        </Carousel>
      )}
    </ForemanDashboardContentStyled>
  );
};
